/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable function-paren-newline */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Badge } from '@folio/stripes-components';
import { Tooltip } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { GrDuplicate } from 'react-icons/gr';
import { useField } from 'react-final-form';

import ConfirmDeleteHoldingModal from '../modals/ConfirmDeleteHoldingModal';
import EditHoldingModal from '../modals/EditHoldingModal';
import DuplicateHoldingModal from '../modals/DuplicateHoldingModal';
import { useFormPageErrorsContext } from '../../../../../../../providers/FormPagesErrorsProvider';

function HoldingAccordionHeader({
  fields,
  index,
  controlledVocab,
  template,
  isDisabledActions,
  holdingRecordId,
  createUndoAction,
  isBatchModeActive,
}) {
  const { updateError } = useFormPageErrorsContext();
  const [openDeleteHoldingModal, setOpenDeleteHoldingModal] = useState(false);
  const [showEditHoldingModal, setShowEditHoldingModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showMultipleDuplicateModal, setShowMultipleDuplicateModal] =
    useState(false);

  // Get holding data and items count
  const holdingData = fields.value[index];
  const items = useField(`itemsData.${holdingRecordId}`);
  const itemsCount = items?.input?.value?.length || 0;
  const itemsArray = items?.input?.value || []; // Get items array for detailed validation

  // Disable actions if batch mode is active or if originally disabled
  const isActionsDisabled = isBatchModeActive || isDisabledActions;

  // Handle single holding duplication
  const handleSingleHoldingDuplicate = (editedValues) => {
    const holdingId = uuidv4();

    const newHolding = {
      ...editedValues,
      id: holdingId,
      holdingHrid: '',
      holdingCopyNumber: fields.value ? fields.value.length + 1 : 1,
    };

    fields.push(newHolding);
  };

  // Handle multiple holdings duplication
  const handleMultipleHoldingsDuplicate = (holdingCount) => {
    const validHoldingCount = Math.max(1, parseInt(holdingCount, 10) || 1);

    for (let i = 0; i < validHoldingCount; i++) {
      const newIndex = i + 1;
      const holdingId = uuidv4();

      fields.push({
        ...holdingData,
        id: holdingId,
        holdingHrid: '',
        holdingCopyNumber: fields.value ? fields.value.length + newIndex : 1,
      });
    }
  };

  // Handle holding deletion
  const handleDeleteHolding = (shouldDeleteItems = false) => {
    // Create undo action for holding deletion
    if (createUndoAction) {
      createUndoAction(
        'DELETE_HOLDING',
        {
          deletedHolding: { ...holdingData },
          holdingIndex: index,
          itemsCount,
          shouldDeleteItems,
        },
        'undoSystem.actions.deleteHolding',
      );
    }

    // If items should be deleted, remove them from the form state
    if (shouldDeleteItems && itemsArray.length > 0) {
      // Clear items for this holding
      items.input.onChange([]);
    }

    // Remove holding from fields
    fields.remove(index);
    setOpenDeleteHoldingModal(false);
    updateError(holdingRecordId, false);
  };

  // Handle holding edit with optional items location update
  const handleEditHolding = (editedValues, updateItemsLocations = false) => {
    fields.update(index, editedValues);
    
    // Update items locations if requested
    if (updateItemsLocations && items?.input?.value?.length > 0) {
      const updatedItems = items.input.value.map(item => ({
        ...item,
        permanentLocation: editedValues.permanentLocationId ? 
          { id: editedValues.permanentLocationId } : item.permanentLocation,
        temporaryLocation: editedValues.temporaryLocationId ? 
          { id: editedValues.temporaryLocationId } : null
      }));
      items.input.onChange(updatedItems);
    }
    
    setShowEditHoldingModal(false);
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      <ConfirmDeleteHoldingModal
        open={openDeleteHoldingModal}
        onClose={() => setOpenDeleteHoldingModal(false)}
        onRemove={handleDeleteHolding}
        holdingRecord={holdingData}
        itemsCount={itemsCount}
        items={itemsArray} // Pass items array for detailed validation
      />

      {/* Edit Holding Modal */}
      <EditHoldingModal
        open={showEditHoldingModal}
        onClose={() => setShowEditHoldingModal(false)}
        onSave={handleEditHolding}
        holdingData={holdingData}
        template={template}
        controlledVocab={controlledVocab}
        itemsFields={{ value: items?.input?.value || [] }}
      />

      {/* Duplicate Single Holding Modal */}
      <DuplicateHoldingModal
        open={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={handleSingleHoldingDuplicate}
        originalHolding={holdingData}
        template={template}
        controlledVocab={controlledVocab}
        currentHoldingsCount={fields.value ? fields.value.length : 0}
      />

      {/* Duplicate Multiple Holdings Modal */}
      <DuplicateHoldingModal
        open={showMultipleDuplicateModal}
        onClose={() => setShowMultipleDuplicateModal(false)}
        onConfirm={handleMultipleHoldingsDuplicate}
        originalHolding={holdingData}
        template={template}
        controlledVocab={controlledVocab}
        currentHoldingsCount={fields.value ? fields.value.length : 0}
        isMultiple
      />

      {/* Duplicate Multiple Button */}
      <Tooltip
        id="holding-accordion-header-duplicate-multiple-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.holdingAccordionHeader.duplicateMultipleBtn.tooltip.msg"
            defaultMessage="Duplicate multiple"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <Button
            buttonStyle="slim"
            onClick={() => setShowMultipleDuplicateModal(true)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isActionsDisabled}
            marginBottom0
          >
            <GrDuplicate size={15} />
          </Button>
        )}
      </Tooltip>

      {/* Duplicate Single Button */}
      <Tooltip
        id="holding-accordion-header-duplicate-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.tooltips.duplicate.msg"
            defaultMessage="Duplicate"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <IconButton
            icon="duplicate"
            onClick={() => setShowDuplicateModal(true)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isActionsDisabled}
          />
        )}
      </Tooltip>

      {/* Edit Holding Button */}
      <Tooltip
        id="holding-accordion-header-edit-tooltip"
        text={
          <FormattedMessage
            id="ui-plugin-inventory-templates.tooltips.edit.msg"
            defaultMessage="Edit"
          />
        }
      >
        {({ ref, ariaIds }) => (
          <IconButton
            icon="edit"
            onClick={() => setShowEditHoldingModal(true)}
            aria-labelledby={ariaIds.text}
            ref={ref}
            disabled={isActionsDisabled}
          />
        )}
      </Tooltip>

      {/* Delete Holding Button */}
      <IconButton
        icon="trash"
        onClick={() => setOpenDeleteHoldingModal(true)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={isActionsDisabled}
      />

      {/* Items Count Badge */}
      <Badge>{itemsCount}</Badge>
    </>
  );
}

HoldingAccordionHeader.propTypes = {
  fields: PropTypes.object,
  index: PropTypes.number,
  controlledVocab: PropTypes.object,
  template: PropTypes.object,
  isDisabledActions: PropTypes.bool,
  holdingRecordId: PropTypes.string,
  createUndoAction: PropTypes.func,
  isBatchModeActive: PropTypes.bool,
};

export default HoldingAccordionHeader;
